import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import Logo from "./Logo";
import { NAV, PROMO_IDS } from "../../constants/testIds";
import { CONTACT, BOOKING_URL } from "../../lib/site";
import { PROMO } from "../../lib/promo";

const links = [
  { to: "/", label: "Home", testid: NAV.home, end: true },
  { to: "/about", label: "About", testid: NAV.about },
  { to: "/services", label: "Services", testid: NAV.services },
  ...(PROMO.enabled
    ? [{ to: `${PROMO.path}?from=nav`, label: "Grand Opening", testid: PROMO_IDS.navLink, promo: true }]
    : []),
  { to: "/blog", label: "Journal", testid: NAV.blog },
  { to: "/contact", label: "Contact", testid: NAV.contact },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Top emergency strip (desktop) */}
      <div className="hidden md:flex w-full bg-forest text-paper text-[11px] tracking-[0.22em] uppercase justify-center items-center gap-3 py-2 paper-grain">
        <span>Dental emergency?</span>
        <a
          data-testid={NAV.emergencyCall}
          href={`tel:${CONTACT.phoneTel}`}
          className="inline-flex items-center gap-2 hover:text-linen transition-colors"
        >
          <Phone size={12} strokeWidth={1.6} />
          Call {CONTACT.phoneLabel}
        </a>
      </div>

      <header
        className={`sticky top-0 z-40 transition-all duration-500 ${
          scrolled
            ? "bg-paper/90 backdrop-blur-md border-b border-umber/10"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            data-testid={NAV.logo}
            onClick={(e) => {
              if (location.pathname === "/") {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            className="flex items-center"
          >
            <Logo />
          </Link>

          <nav className="hidden md:flex items-center gap-8 lg:gap-9">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={l.testid}
                onClick={(e) => {
                  if (l.to === location.pathname) {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }}
                className={({ isActive }) =>
                  `text-[13px] tracking-[0.18em] uppercase transition-colors whitespace-nowrap ${
                    l.promo
                      ? isActive
                        ? "text-forest border-b border-forest pb-0.5"
                        : "text-forest/90 hover:text-forest border-b border-forest/30 hover:border-forest pb-0.5"
                      : isActive
                      ? "text-forest"
                      : "text-umber/80 hover:text-forest"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:block">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={NAV.cta}
              className="bg-forest text-paper rounded-full px-6 py-2.5 text-[12px] tracking-[0.18em] uppercase hover:bg-sage transition-all duration-300"
            >
              Book Online
            </a>
          </div>

          <button
            type="button"
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-umber p-3 min-h-[44px] min-w-[44px] inline-flex items-center justify-center"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile drawer */}
        {open && (
          <div className="md:hidden bg-paper border-t border-umber/10">
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  data-testid={`${l.testid}-mobile`}
                  onClick={(e) => {
                    if (l.to === location.pathname) {
                      e.preventDefault();
                      setOpen(false);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }
                  }}
                  className={({ isActive }) =>
                    `font-display text-2xl py-2 min-h-[44px] flex items-center ${
                      l.promo
                        ? "text-forest font-display-italic"
                        : isActive
                        ? "text-forest"
                        : "text-umber"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="mt-2 inline-block bg-forest text-paper rounded-full px-6 py-3 text-[12px] tracking-[0.18em] uppercase text-center"
              >
                Book Online
              </a>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
